import moment from 'moment';

import { FilterAndSortType, BTSessionStatus } from '../../../../constants';
import { CQAReviewStatus, QaFilter } from '../constants';

import type {
  IFilterOutput,
  ISessionMinifiedWithClaimHistory,
} from '../../../../utils/types';

export const QAFilterOptions = {
  [QaFilter.DocumentStatus]: {
    type: FilterAndSortType.Radio,
    label: 'Document status',
    secondaryLabel: 'Prioritize by',
    dataTestid: 'document-status',
    options: [
      { label: 'Completed', id: 'completed', dataTestId: 'sortByCompletedStatus' },
      {
        label: 'Edits requested',
        id: 'edits-requested',
        dataTestId: 'sortByEditsRequestedStatus',
      },
      {
        label: 'Awaiting review',
        id: 'awaiting-review',
        dataTestId: 'sortByAwaitingReviewStatus',
      },
      {
        label: 'Awaiting finalization',
        id: 'awaiting-finalization',
        dataTestId: 'sortByawaitingFinalizationStatus',
      },
    ],
    order: 1,
  },
  [QaFilter.SignatureStatus]: {
    type: FilterAndSortType.Radio,
    label: 'Signature status',
    secondaryLabel: 'Prioritize by',

    options: [
      { label: 'Signed', id: 'signed', dataTestId: 'signedSortOption' },
      { label: 'Not signed', id: 'not-signed', dataTestId: 'notSignedSortOption' },
    ],
    order: 2,
  },
  [QaFilter.QaDate]: {
    type: FilterAndSortType.Radio,
    label: 'QA Date',
    secondaryLabel: 'Prioritize by',

    options: [
      { label: 'Most recent', id: 'most-recent', dataTestId: 'mostRecentSortOption' },
      { label: 'Least recent', id: 'least-recent', dataTestId: 'leastRecentSortOption' },
    ],
    order: 3,
  },
};

export const filterQACards = (
  filterOutput: IFilterOutput,
  sessionsList: Array<ISessionMinifiedWithClaimHistory>,
): Array<ISessionMinifiedWithClaimHistory> => {
  let sortOrder: (number | string)[] = [];
  for (const name in filterOutput) {
    if (filterOutput[name] && filterOutput[name][0]) {
      if (name === QaFilter.DocumentStatus) {
        if (filterOutput[name][0].id === 'completed') {
          sortOrder = [
            BTSessionStatus.Completed,
            BTSessionStatus.EditsRequested,
            BTSessionStatus.AwaitingFinalization,
            BTSessionStatus.AwaitingReview,
          ];
        } else if (filterOutput[name][0].id === 'edits-requested') {
          sortOrder = [
            BTSessionStatus.EditsRequested,
            BTSessionStatus.AwaitingFinalization,
            BTSessionStatus.AwaitingReview,
            BTSessionStatus.Completed,
          ];
        } else if (filterOutput[name][0].id === 'awaiting-review') {
          sortOrder = [
            BTSessionStatus.AwaitingReview,
            BTSessionStatus.AwaitingFinalization,
            BTSessionStatus.EditsRequested,
            BTSessionStatus.Completed,
          ];
        }
        if (filterOutput[name][0].id === 'awaiting-finalization') {
          sortOrder = [
            BTSessionStatus.AwaitingFinalization,
            BTSessionStatus.AwaitingReview,
            BTSessionStatus.EditsRequested,
            BTSessionStatus.Completed,
          ];
        }

        return sortOrder.reduce(
          (finalList: Array<ISessionMinifiedWithClaimHistory>, filterValue) => {
            return [
              ...finalList,
              ...sessionsList
                .filter((session) => {
                  if (filterValue === BTSessionStatus.AwaitingFinalization) {
                    return (
                      session.status === BTSessionStatus.AwaitingReview &&
                      Boolean(session.clinical_qa_review) &&
                      session.clinical_qa_review?.status === CQAReviewStatus.Acknowledged
                    );
                  }

                  if (filterValue === BTSessionStatus.AwaitingReview) {
                    return (
                      session.status === BTSessionStatus.AwaitingReview &&
                      (!session.clinical_qa_review ||
                        session.clinical_qa_review?.status === CQAReviewStatus.InProgress)
                    );
                  }
                  return session.status === filterValue;
                })
                .sort(
                  (sessionA, sessionB) =>
                    moment(sessionA.date_end).unix() - moment(sessionB.date_end).unix(),
                ),
            ];
          },
          [],
        );
      } else if (name === QaFilter.SignatureStatus) {
        if (filterOutput[name][0].id === 'signed') {
          sortOrder = [CQAReviewStatus.Acknowledged, CQAReviewStatus.AwaitingAcknowledgement];
        } else if (filterOutput[name][0].id === 'not-signed') {
          sortOrder = [CQAReviewStatus.AwaitingAcknowledgement, CQAReviewStatus.Acknowledged];
        }

        // awaiting session items
        const defaultList = [
          ...sessionsList
            .filter((session) => !session.clinical_qa_review)
            .sort(
              (sessionA, sessionB) =>
                moment(sessionA.date_end).unix() - moment(sessionB.date_end).unix(),
            ),
        ];

        //qa sessioned items
        const qaSessionedItems = sessionsList
          .filter((session) => Boolean(session.clinical_qa_review))
          .sort(
            (sessionA, sessionB) =>
              moment(sessionA.date_end).unix() - moment(sessionB.date_end).unix(),
          );

        const filteredList = sortOrder.reduce(
          (finalList: Array<ISessionMinifiedWithClaimHistory>, filterValue) => {
            return [
              ...finalList,
              ...qaSessionedItems
                .filter((session) => session.clinical_qa_review?.status === filterValue)
                .sort(
                  (sessionA, sessionB) =>
                    moment(sessionA.date_end).unix() - moment(sessionB.date_end).unix(),
                ),
            ];
          },
          [],
        );

        return [...filteredList, ...defaultList];
      } else if (name === QaFilter.QaDate) {
        const defaultList = [...sessionsList.filter((session) => !session.clinical_qa_review)];

        const filteredList = sessionsList
          .filter(
            (
              session,
            ): session is ISessionMinifiedWithClaimHistory & {
              clinical_qa_review: NonNullable<ISessionMinifiedWithClaimHistory['clinical_qa_review']>;
            } => Boolean(session.clinical_qa_review),
          )
          .sort((sessionA, sessionB) => {
            const dateA = sessionA.clinical_qa_review.date_reviewed;
            const dateB = sessionB.clinical_qa_review.date_reviewed;

            if (!dateA || !dateB) return 0;

            if (moment(dateA).isAfter(moment(dateB)))
              return filterOutput[name][0].id === 'most-recent' ? -1 : 1;
            else if (moment(dateA).isBefore(moment(dateB)))
              return filterOutput[name][0].id === 'most-recent' ? 1 : -1;
            return 0;
          });

        return [...filteredList, ...defaultList];
      }
    }
  }

  return sessionsList;
};
